import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import { RefreshControl, View } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { useMutation } from '@apollo/client';
import { compose } from 'redux';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { GET_MACHINE_DETAIL_MOBILE, MODIFY_MACHINE } from '../../../common/graphql/machine';
import { MODIFY_PLACE } from '../../../common/graphql/place';
import { POS_TYPE, ROLE_PERMISSIONS } from '../../../common/constants';
import { DetailContainerNew } from '../../../common/containers';
import { getFullMachineName } from '../../../common/utils/machine';

import Text from '../../components/text/Text';
import { VisualDirectionPhotoGrid } from '../../components/image';
import { TextInputEditInPlace } from '../../components/input';
import { DetailContainer } from '../../containers';
import { Card, EmptyState, PrimaryButton, Screen, SecondaryButton, YesNo } from '../../design/components';
import withMediaPicker from '../../hocs/withMediaPicker';
import useBackLabel from '../../hooks/useBackLabel';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useUserContext } from '../../../common/utils/context';
import { MODIFY_LOCATION_MOBILE } from '../../graphql/location';
import { MapLink, openLocationInMaps } from '../map/MapLink';
import DirectionsField from './DirectionsField';
import { createMachineDirectionsViewStyles } from './MachineDirectionsView.styles';
import { hasDirectionsContent, hasMapDetails } from './machineDirectionsHelpers';
import useVisualDirectionPhotos from './useVisualDirectionPhotos';

const EDITING_CARD = {
	SITE: 'site',
	POS: 'pos',
};

const MachineDirectionsRaw = ({
	captureMedia = () => {},
	capturedMediaLink,
	data: machine,
	forceReadOnly = false,
	modifyItem: modifyMachine,
	processingMedia = false,
	refetch,
}) => {
	const styles = useThemedStyles(createMachineDirectionsViewStyles);
	const backLabel = useBackLabel();
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [editingCard, setEditingCard] = useState(null);
	const { hasPermission } = useUserContext();
	const [modifyLocation] = useMutation(MODIFY_LOCATION_MOBILE);
	const [modifyPlace] = useMutation(MODIFY_PLACE);

	const canEditLocation = !forceReadOnly && hasPermission(ROLE_PERMISSIONS.ADD_EDIT_LOCATIONS);
	const canEditPlace = !forceReadOnly && hasPermission(ROLE_PERMISSIONS.ADD_EDIT_PLACES);
	const canEditMachine = !forceReadOnly && Boolean(modifyMachine) && hasPermission(ROLE_PERMISSIONS.ADD_EDIT_MACHINES);
	const hasValidPlace = Boolean(machine.place && !machine.place.destroyed);
	const isMarket = machine.type === POS_TYPE.MARKET;
	const posName = isMarket ? 'Market' : 'Machine';
	const machineName = getFullMachineName(machine, machine.place, machine.location);
	const mapLocation = useMemo(() => {
		if (!machine.location && !machine.coordinates?.length) {
			return null;
		}

		return {
			...(machine.location || {}),
			coordinates: machine.coordinates?.length ? machine.coordinates : machine.location?.coordinates,
		};
	}, [machine.coordinates, machine.location]);
	const placeAccess = useMemo(() => (hasValidPlace
		? {
			...machine.place,
			directions: machine.place.directions,
		}
		: null
	), [hasValidPlace, machine.place]);

	const modifySelectedLocation = useCallback(async values => {
		if (!machine.location?.id) {
			return;
		}

		await modifyLocation({
			variables: {
				input: {
					id: machine.location.id,
					...values,
				},
			},
		});
	}, [machine.location?.id, modifyLocation]);

	const modifySelectedPlace = useCallback(async values => {
		if (!hasValidPlace || !machine.place?.id) {
			return;
		}

		await modifyPlace({
			variables: {
				input: {
					id: machine.place.id,
					...values,
				},
			},
		});
	}, [hasValidPlace, machine.place?.id, modifyPlace]);

	const {
		addVisualDirectionPhoto,
		locationVisualDirectionPhotoUrls,
		locationVisualDirectionPhotos,
		pendingVisualDirectionOwner,
		placeVisualDirectionPhotoUrls,
		placeVisualDirectionPhotos,
		removeLocationVisualDirectionPhoto,
		removePlaceVisualDirectionPhoto,
		updateVisualDirectionPhotoComment,
	} = useVisualDirectionPhotos({
		captureMedia,
		capturedMediaLink,
		hasValidPlace,
		machine,
		modifySelectedLocation,
		modifySelectedPlace,
		processingMedia,
	});

	const hasLocationDetails = Boolean(machine.location ||
		hasMapDetails(mapLocation) ||
		machine.location?.accessHours ||
		hasDirectionsContent(machine.location) ||
		canEditLocation);
	const hasPosDetails = Boolean(machine.place || machine.accessHours || hasDirectionsContent(placeAccess) || canEditMachine || canEditPlace);
	const hasAnyDirections = hasLocationDetails || hasPosDetails;
	const canEditSite = Boolean(machine.location && canEditLocation);
	const canEditPlaceDetails = Boolean(hasValidPlace && canEditPlace);
	const canEditPos = canEditMachine || canEditPlaceDetails;
	const hasSiteValues = Boolean(typeof machine.location?.requiresSignIn === 'boolean' ||
		machine.location?.accessHours ||
		machine.location?.directions);
	const hasPosValues = Boolean(machine.accessHours || placeAccess?.directions);

	const refreshPage = useCallback(async () => {
		if (!refetch) {
			return;
		}

		setIsRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setIsRefreshing(false);
		}
	}, [refetch]);

	const renderPhotos = ({ photos, photoUrls, isEditing, onRemovePhoto }) => {
		if (!photos?.length && !photoUrls?.length) {
			return null;
		}

		return (
			<View style={styles.field}>
				<Text style={styles.fieldLabel}>Photos</Text>
				<VisualDirectionPhotoGrid
					canEdit={isEditing}
					emptyText=""
					photos={photos}
					photoUrls={photoUrls}
					onRemovePhoto={onRemovePhoto}
					onUpdatePhotoComment={updateVisualDirectionPhotoComment}
				/>
			</View>
		);
	};

	// Values read first (SPEC: Directions mockup); editors open the fields of one card.
	const renderActions = ({ card, hasValues, canEditDetails, canAddPhoto, onAddPhoto }) => {
		const isEditing = editingCard === card;
		return (
			<View style={styles.actions}>
				{canEditDetails && (
					<SecondaryButton
						label={isEditing ? 'Done' : (hasValues ? 'Edit' : 'Add directions')}
						icon={isEditing ? 'check' : undefined}
						onPress={() => setEditingCard(isEditing ? null : card)}
						style={styles.action}
					/>
				)}
				{canAddPhoto && (
					<SecondaryButton
						label={processingMedia && pendingVisualDirectionOwner ? 'Adding photo…' : 'Add photo'}
						onPress={onAddPhoto}
						disabled={processingMedia}
						style={styles.action}
					/>
				)}
			</View>
		);
	};

	const isEditingSite = editingCard === EDITING_CARD.SITE;
	const isEditingPos = editingCard === EDITING_CARD.POS;
	const signInValue = typeof machine.location?.requiresSignIn === 'boolean' ? machine.location.requiresSignIn : null;

	return (
		<Screen
			headerRight={<ServiceNoteButton />}
			backLabel={backLabel}
			heading="Directions"
			subheading={[machine.location?.name, machine.place?.name, machine.assetId].filter(Boolean).join(' · ') || machineName}
			refreshControl={(
				<RefreshControl
					refreshing={isRefreshing}
					onRefresh={refreshPage}
				/>
			)}
		>
			{hasLocationDetails && (
				<Card style={styles.card}>
					<Text style={styles.cardTitle} accessibilityRole="header">Getting to the site</Text>
					<MapLink location={mapLocation} asText={true} />
					{hasMapDetails(mapLocation) && (
						<PrimaryButton label="Open in Maps" icon="map-pin" onPress={() => openLocationInMaps(mapLocation)} />
					)}
					{isEditingSite ? (
						<>
							<View style={styles.field}>
								<Text style={styles.fieldLabel}>Site sign-in</Text>
								<YesNo
									value={signInValue}
									onChange={requiresSignIn => modifySelectedLocation({ requiresSignIn })}
									accessibilityLabel="Requires site sign-in"
									style={styles.yesNo}
								/>
							</View>
							{signInValue === true && (
								<View style={styles.field}>
									<Text style={styles.fieldLabel}>Sign-in instructions</Text>
									<TextInputEditInPlace
										value={machine.location?.signInInstructions}
										placeholder="None specified"
										field="signInInstructions"
										onConfirm={modifySelectedLocation}
										style={styles.multilineInput}
										multiline={true}
										numberOfLines={4}
									/>
								</View>
							)}
							<View style={styles.field}>
								<Text style={styles.fieldLabel}>Access hours</Text>
								<TextInputEditInPlace
									value={machine.location?.accessHours}
									placeholder="None specified"
									field="accessHours"
									onConfirm={modifySelectedLocation}
								/>
							</View>
							<View style={styles.field}>
								<Text style={styles.fieldLabel}>Directions</Text>
								<TextInputEditInPlace
									value={machine.location?.directions}
									placeholder="No site directions yet"
									field="directions"
									onConfirm={modifySelectedLocation}
									style={styles.multilineInput}
									multiline={true}
									numberOfLines={4}
								/>
							</View>
						</>
					) : (
						<>
							{Boolean(machine.location) && (
								<DirectionsField
									label="Site sign-in"
									value={signInValue === null ? null : (signInValue ? 'Yes' : 'No')}
									placeholder="Not recorded"
								/>
							)}
							{signInValue === true && (
								<DirectionsField label="Sign-in instructions" value={machine.location.signInInstructions} placeholder="None specified" />
							)}
							<DirectionsField
								label="Access hours"
								value={machine.location?.accessHours}
								placeholder={machine.location ? 'None specified' : 'No location selected'}
							/>
							<DirectionsField
								label="Directions"
								value={machine.location?.directions}
								placeholder={machine.location ? 'No site directions yet' : 'No location selected'}
							/>
						</>
					)}
					{renderPhotos({
						photos: locationVisualDirectionPhotos,
						photoUrls: locationVisualDirectionPhotoUrls,
						isEditing: isEditingSite,
						onRemovePhoto: removeLocationVisualDirectionPhoto,
					})}
					{canEditSite && renderActions({
						card: EDITING_CARD.SITE,
						hasValues: hasSiteValues,
						canEditDetails: true,
						canAddPhoto: true,
						onAddPhoto: addVisualDirectionPhoto('location'),
					})}
				</Card>
			)}

			{hasPosDetails && (
				<Card style={styles.card}>
					<Text style={styles.cardTitle} accessibilityRole="header">{`Finding the ${posName.toLowerCase()}`}</Text>
					{isEditingPos && canEditMachine ? (
						<View style={styles.field}>
							<Text style={styles.fieldLabel}>Access hours</Text>
							<TextInputEditInPlace
								value={machine.accessHours}
								placeholder="None specified"
								field="accessHours"
								onConfirm={modifyMachine}
							/>
						</View>
					) : (
						<DirectionsField label="Access hours" value={machine.accessHours} placeholder="None specified" />
					)}
					{isEditingPos && canEditPlaceDetails ? (
						<View style={styles.field}>
							<Text style={styles.fieldLabel}>Directions</Text>
							<TextInputEditInPlace
								value={placeAccess?.directions}
								placeholder={`No ${posName.toLowerCase()} directions yet`}
								field="directions"
								onConfirm={modifySelectedPlace}
								style={styles.multilineInput}
								multiline={true}
								numberOfLines={4}
							/>
						</View>
					) : (
						<DirectionsField
							label="Directions"
							value={placeAccess?.directions}
							placeholder={hasValidPlace ? `No ${posName.toLowerCase()} directions yet` : 'Select a place first'}
						/>
					)}
					{renderPhotos({
						photos: placeVisualDirectionPhotos,
						photoUrls: placeVisualDirectionPhotoUrls,
						isEditing: isEditingPos && canEditPlaceDetails,
						onRemovePhoto: removePlaceVisualDirectionPhoto,
					})}
					{canEditPos && renderActions({
						card: EDITING_CARD.POS,
						hasValues: hasPosValues,
						canEditDetails: true,
						canAddPhoto: canEditPlaceDetails,
						onAddPhoto: addVisualDirectionPhoto('place'),
					})}
				</Card>
			)}

			{hasAnyDirections
				? (canEditSite || canEditPos) && (
					<Text style={styles.footnote}>{`Directions and photos you add here help the next driver find this ${posName.toLowerCase()}.`}</Text>
				)
				: <EmptyState title="No directions yet" message="No directions are set." />}
		</Screen>
	);
};

const MachineDirections = compose(DetailContainer, withMediaPicker)(MachineDirectionsRaw);
const MachineDirectionsWithMediaPicker = withMediaPicker(MachineDirectionsRaw);
const MachineDirectionsWithFallbackRaw = ({
	data,
	error,
	fallbackMachine,
	forceReadOnly = false,
	loading,
	...props
}) => {
	const machine = data || fallbackMachine;

	if (!machine) {
		return (
			<Screen
				headerRight={<ServiceNoteButton />}
				heading="Directions"
				isLoading={loading}
				error={error ? 'Something went wrong loading this page. Check your connection and try again.' : undefined}
			>
				<EmptyState message="Nothing to show here yet" />
			</Screen>
		);
	}

	return (
		<MachineDirectionsWithMediaPicker
			{...props}
			data={machine}
			forceReadOnly={forceReadOnly || !data}
		/>
	);
};
const MachineDirectionsWithFallback = DetailContainerNew(MachineDirectionsWithFallbackRaw);

const MachineDirectionsView = ({ forceReadOnly = false, machineId: propMachineId }) => {
	const route = useRoute();
	const routeMachine = route.params?.machine;
	const fallbackMachine = route.params?.fallbackMachine;
	const routeForceReadOnly = Boolean(route.params?.forceReadOnly);
	const machineId = propMachineId || route.params?.machineId;

	if (routeMachine) {
		return (
			<MachineDirectionsWithMediaPicker
				data={routeMachine}
				forceReadOnly={forceReadOnly || routeForceReadOnly}
				s3Path="images/places/"
			/>
		);
	}

	if (fallbackMachine) {
		return (
			<MachineDirectionsWithFallback
				fallbackMachine={fallbackMachine}
				forceReadOnly={forceReadOnly || routeForceReadOnly}
				id={machineId}
				query={GET_MACHINE_DETAIL_MOBILE}
				modifyMutation={MODIFY_MACHINE}
				s3Path="images/places/"
			/>
		);
	}

	return (
		<MachineDirections
			forceReadOnly={forceReadOnly || routeForceReadOnly}
			id={machineId}
			query={GET_MACHINE_DETAIL_MOBILE}
			modifyMutation={MODIFY_MACHINE}
			s3Path="images/places/"
		/>
	);
};

export default MachineDirectionsView;
